import {
  DeviceGroupPermissions,
  SoftwareSeatPermissions,
} from '@order-management/roles';
import {
  DeferredResponse,
  Device,
  DeviceGroup,
  DeviceGroupData,
  DeviceGroupsCriteria,
  DeviceModel,
  InputType,
  LicensingProductGroup,
  MyDevicesData,
  PaginatedAPIResponse,
  SortDirection,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element, Icon, Section } from 'react-bulma-components';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa6';
import {
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigate,
} from 'react-router-dom';
import { fetchDeviceModels } from '../../devices/devices-service';
import BackButton from '../../shared/back-button/back-button';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import useIsEmpty from '../../shared/hooks/is-empty';
import usePermissions from '../../shared/hooks/permissions';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { fetchSoftwareTitles } from '../../software/software-service';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import FeedbackMessage from '../../ui/feedback-message/feedback-message';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { DeviceForSoftwareAssignmentStorageService } from '../device-for-software-assignment-storage-service';
import DeviceGroupInboxTable from '../device-group-inbox-table/device-group-inbox-table';
import { fetchDeviceGroups } from '../device-groups-service';
import { SoftwareTitlesForAssignmentStorageService } from '../software-titles-for-assignment-storage-service';

const devicesStorage = new DeviceForSoftwareAssignmentStorageService();
const softwareStorage = new SoftwareTitlesForAssignmentStorageService();

const FETCH_DEVICES_ERROR_MESSAGE =
  'My Devices could not be fetched. Please try again';
const MULTIPLE_DEVICE_TYPES_ERROR_MESSAGE =
  'You can only manage one device type at a time';
const NO_DEVICE_SELECTED_ERROR_MESSAGE =
  'You must select at least one device to manage software';

const initialCriteria: DeviceGroupsCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
  excludeEmptyGroups: true,
  includeUnmanageableByPermissions: true,
  includeHiddenGroups: false,
};

export const loader: LoaderFunction = async () => {
  const response = fetchDeviceGroups(initialCriteria);

  return defer({ response });
};

export function DeviceGroupsDashboardCardPageContent() {
  const navigate = useNavigate();
  const userHasPermissions = usePermissions();
  const myDeviceGroupsInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<DeviceGroupData>;
  const [myDeviceGroupsDataResponse, setMyDeviceGroupsDataResponse] = useState<
    PaginatedAPIResponse<DeviceGroupData>
  >(myDeviceGroupsInitialResponse);
  const [myDeviceGroups, setMyDeviceGroups] = useState<DeviceGroupData[]>(
    myDeviceGroupsInitialResponse.data
  );
  const [deviceGroupsFilterCriteria, setDeviceGroupsFilterCriteria] =
    useState<DeviceGroupsCriteria>(initialCriteria);
  const [baseDeviceGroupsCriteria, setBaseDeviceGroupsCriteria] =
    useState<DeviceGroupsCriteria>(initialCriteria);
  const {
    executeHttpRequest: executeFetchMyDeviceGroupsRequest,
    isLoading: isLoadingMyDeviceGroups,
  } = useHttpRequest();
  const { executeHttpRequest: executeFetchMoreMyDeviceGroups } =
    useHttpRequest();
  const [selectedDevicesByDeviceGroup, setSelectedDevicesByDeviceGroup] =
    useState<Map<string, Device[]>>(new Map<string, Device[]>());
  const [manageSoftwareButtonPressed, setManageSoftwareButtonPressed] =
    useState(false);
  const isDataEmpty = useIsEmpty(myDeviceGroupsInitialResponse.data);

  const selectedDevices = useMemo(
    () => Array.from(selectedDevicesByDeviceGroup.values()).flat(),
    [selectedDevicesByDeviceGroup]
  );

  const hasMoreDeviceGroups = useMemo(
    () => !!myDeviceGroupsDataResponse.hasMore,
    [myDeviceGroupsDataResponse]
  );

  const filtersData = useMemo(() => {
    return {
      salesOrders: {
        title: 'Sales order',
        appliedCount: deviceGroupsFilterCriteria.salesOrders?.length,
        value: deviceGroupsFilterCriteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      deviceTypes: {
        title: 'Device type',
        appliedCount: deviceGroupsFilterCriteria.deviceTypes?.length,
        value: deviceGroupsFilterCriteria.deviceTypes,
        input: {
          type: InputType.SELECT,
          valueKey: 'deviceTypes',
          fetchItems: fetchDeviceModels,
          resolveOption: (item: DeviceModel) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      softwareTitles: {
        title: 'Software',
        appliedCount: deviceGroupsFilterCriteria.softwareTitles?.length,
        value: deviceGroupsFilterCriteria.softwareTitles,
        input: {
          type: InputType.SELECT,
          valueKey: 'softwareTitles',
          fetchItems: fetchSoftwareTitles,
          resolveOption: (
            item: Pick<LicensingProductGroup, 'id' | 'displayName'>
          ) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      serialNumbers: {
        title: 'Serial number',
        appliedCount: deviceGroupsFilterCriteria.serialNumbers?.length,
        value: deviceGroupsFilterCriteria.serialNumbers,
        input: {
          type: InputType.FILTER_CRITERIA,
          valueKey: 'serialNumbers',
          filterValueKey: 'serialNumbers',
          placeholder: 'Search by Serial number',
        },
      },
    };
  }, [
    deviceGroupsFilterCriteria.deviceTypes,
    deviceGroupsFilterCriteria.salesOrders,
    deviceGroupsFilterCriteria.serialNumbers,
    deviceGroupsFilterCriteria.softwareTitles,
  ]) as FiltersData<DeviceGroupsCriteria>;

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const showNoDeviceSelectedError = useMemo(
    () => selectedDevices.length === 0,
    [selectedDevices]
  );

  const showMultipleDeviceModelsError = useMemo(() => {
    if (selectedDevices.length === 0) return false;

    const deviceModels = selectedDevices.map((device) => device.model.id);
    return deviceModels.some((deviceModel) => deviceModel !== deviceModels[0]);
  }, [selectedDevices]);

  const showErrorText = useMemo(
    () =>
      (showMultipleDeviceModelsError || showNoDeviceSelectedError) &&
      manageSoftwareButtonPressed,
    [
      showMultipleDeviceModelsError,
      showNoDeviceSelectedError,
      manageSoftwareButtonPressed,
    ]
  );

  const errorText = useMemo(() => {
    if (showMultipleDeviceModelsError) {
      return MULTIPLE_DEVICE_TYPES_ERROR_MESSAGE;
    } else if (showNoDeviceSelectedError) {
      return NO_DEVICE_SELECTED_ERROR_MESSAGE;
    }
  }, [showMultipleDeviceModelsError, showNoDeviceSelectedError]);

  const handleOnManageSoftware = useCallback(() => {
    setManageSoftwareButtonPressed(true);
    if (!showNoDeviceSelectedError && !showMultipleDeviceModelsError) {
      devicesStorage.save(selectedDevices);
      softwareStorage.remove();

      const modelId = selectedDevices[0].model.id;
      navigate(`/device-models/${modelId}/software/select`);
    }
  }, [
    navigate,
    selectedDevices,
    showMultipleDeviceModelsError,
    showNoDeviceSelectedError,
  ]);

  const resolveBaseDeviceGroupsCriteria = useCallback(
    (criteria: DeviceGroupsCriteria) => ({
      itemsPerPage: criteria.itemsPerPage,
      pageNumber: criteria.pageNumber,
      search: criteria.search,
      sortBy: criteria.sortBy,
      sortDirection: criteria.sortDirection,
      excludeEmptyGroups: criteria.excludeEmptyGroups,
      includeUnmanageableByPermissions:
        criteria.includeUnmanageableByPermissions,
      includeHiddenGroups: criteria.includeHiddenGroups,
    }),
    []
  );

  const hasBaseDeviceGroupsCriteriaChanged = useCallback(
    (newCriteria: DeviceGroupsCriteria) => {
      const keys = Object.keys(
        initialCriteria
      ) as (keyof DeviceGroupsCriteria)[];
      return keys.some(
        (key) => newCriteria[key] !== baseDeviceGroupsCriteria[key]
      );
    },
    [baseDeviceGroupsCriteria]
  );

  const fetchMoreDeviceGroups = useCallback(
    () =>
      executeFetchMoreMyDeviceGroups({
        asyncFunction: async () => {
          const nextPage = deviceGroupsFilterCriteria.pageNumber + 1;
          const updatedDeviceGroupCriteria = {
            ...deviceGroupsFilterCriteria,
            pageNumber: nextPage,
          };
          const myDeviceGroupsResponse = await fetchDeviceGroups(
            resolveBaseDeviceGroupsCriteria(updatedDeviceGroupCriteria)
          );
          setDeviceGroupsFilterCriteria(updatedDeviceGroupCriteria);
          setMyDeviceGroups((prevDeviceGroups) =>
            prevDeviceGroups.concat(myDeviceGroupsResponse.data)
          );
          setMyDeviceGroupsDataResponse(myDeviceGroupsResponse);
        },
      }),
    [
      deviceGroupsFilterCriteria,
      executeFetchMoreMyDeviceGroups,
      resolveBaseDeviceGroupsCriteria,
    ]
  );

  const handleManageDeviceClick = useCallback(
    (deviceGroupId: string, newDevices: Device[], add: boolean) => {
      const userHasDeviceGroupUpdatePermissions = userHasPermissions({
        permissions: DeviceGroupPermissions.DEVICE_GROUPS_UPDATE,
      });

      if (userHasDeviceGroupUpdatePermissions) {
        setSelectedDevicesByDeviceGroup((devicesByDeviceGroup) => {
          const updatedDevicesByDeviceGroup = new Map(devicesByDeviceGroup);

          const deviceGroupDevices =
            updatedDevicesByDeviceGroup.get(deviceGroupId);
          if (add) {
            if (!deviceGroupDevices) {
              updatedDevicesByDeviceGroup.set(deviceGroupId, newDevices);
            } else {
              const updatedDevices = [...deviceGroupDevices, ...newDevices];
              updatedDevicesByDeviceGroup.set(
                deviceGroupId,
                updatedDevices.filter(
                  (device, index) =>
                    index ===
                    updatedDevices.findIndex((d) => d.id === device.id)
                )
              );
            }
          } else {
            if (deviceGroupDevices) {
              const newDevicesId = newDevices.map((device) => device.id);
              updatedDevicesByDeviceGroup.set(
                deviceGroupId,
                deviceGroupDevices.filter(
                  (device) => !newDevicesId.includes(device.id)
                )
              );
            }
          }
          return updatedDevicesByDeviceGroup;
        });
      }
    },
    [userHasPermissions]
  );

  const fetchMyDeviceGroups = useCallback(
    (deviceGroupsFilterCriteria: DeviceGroupsCriteria) =>
      executeFetchMyDeviceGroupsRequest({
        asyncFunction: async () => {
          const myDeviceGroupsResponse = await fetchDeviceGroups(
            resolveBaseDeviceGroupsCriteria(deviceGroupsFilterCriteria)
          );
          setMyDeviceGroups(myDeviceGroupsResponse.data);
        },
        customErrorMessage: FETCH_DEVICES_ERROR_MESSAGE,
      }),
    [executeFetchMyDeviceGroupsRequest, resolveBaseDeviceGroupsCriteria]
  );

  const handleTableFilterSubmit = useCallback(
    async (value: DeviceGroupsCriteria) => {
      const { itemsPerPage, pageNumber } = initialCriteria;
      const updatedCriteria = {
        ...value,
        itemsPerPage,
        pageNumber,
      };
      setDeviceGroupsFilterCriteria(updatedCriteria);

      const updatedBaseDeviceGroupsCriteria =
        resolveBaseDeviceGroupsCriteria(updatedCriteria);
      const criteriaChanged = hasBaseDeviceGroupsCriteriaChanged(
        updatedBaseDeviceGroupsCriteria
      );

      if (criteriaChanged) {
        setBaseDeviceGroupsCriteria(updatedBaseDeviceGroupsCriteria);
        await fetchMyDeviceGroups(updatedCriteria);
      }
    },
    [
      fetchMyDeviceGroups,
      hasBaseDeviceGroupsCriteriaChanged,
      resolveBaseDeviceGroupsCriteria,
    ]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof DeviceGroupsCriteria)[]) => {
      const newData = { ...deviceGroupsFilterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [deviceGroupsFilterCriteria, handleTableFilterSubmit]
  );

  const isInvalidSelection = useCallback(
    (devices: Device[]) => {
      let isUniqueDeviceModelSelected = true;

      devices.forEach((device) => {
        isUniqueDeviceModelSelected =
          isUniqueDeviceModelSelected &&
          !selectedDevices.some(
            (selectedDevice) =>
              selectedDevice.id === device.id &&
              selectedDevices.some(
                (device) => device.model.id !== selectedDevice.model.id
              )
          );
      });

      return !isUniqueDeviceModelSelected;
    },
    [selectedDevices]
  );

  const navigateToMyDevices = useCallback(() => {
    navigate('/my-devices');
  }, [navigate]);

  const navigateToMySoftware = useCallback(() => {
    navigate('/my-software');
  }, [navigate]);

  const Header = useCallback(
    () => (
      <Columns marginless display="flex">
        <Columns.Column>
          <BackButton onClick={navigateToMySoftware} />
          <h1 className="is-size-3 has-text-weight-light">Device groups</h1>
          <h3 className="is-size-5 has-text-weight-light">
            Select to which devices you want to assign the new software seats
          </h3>
        </Columns.Column>
        <Columns.Column display="flex" justifyContent="flex-end">
          <If condition={!isDataEmpty}>
            <Button color="primary-dark" onClick={handleOnManageSoftware}>
              <span>Assign new licenses</span>
              <Icon>
                <FaArrowRight />
              </Icon>
            </Button>
          </If>
        </Columns.Column>
      </Columns>
    ),
    [handleOnManageSoftware, isDataEmpty, navigateToMySoftware]
  );

  const EmptyState = useCallback(
    () => (
      <Section
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingless
        className="gap-6"
      >
        <Element display="flex" flexDirection="column" alignItems="center">
          <span className="is-size-5 has-text-weight-light">
            No device groups yet
          </span>
          <span>
            You need to group your devices in order to manage their software
          </span>
        </Element>
        <Button color="primary-dark" onClick={navigateToMyDevices}>
          <Icon>
            <FaArrowLeft />
          </Icon>
          <span>Back to My Devices</span>
        </Button>
      </Section>
    ),
    [navigateToMyDevices]
  );

  return (
    <BoxLayout className="is-min-height-80 mx-10 mb-4 my-4" header={<Header />}>
      <Conditional condition={isDataEmpty}>
        <Conditional.True>
          <EmptyState />
        </Conditional.True>
        <Conditional.False>
          <If condition={showErrorText}>
            <Element mb={2}>
              <FeedbackMessage>{errorText}</FeedbackMessage>
            </Element>
          </If>
          <Element display="flex" className="w-full">
            <FilterBar
              initialData={initialCriteria}
              data={deviceGroupsFilterCriteria}
              filtersData={filtersData}
              onFilterSubmit={handleTableFilterSubmit}
              onRemoveFilter={onRemoveFilters}
            />
          </Element>
          <DeviceGroupInboxTable
            deviceGroups={myDeviceGroups}
            fetchMoreDeviceGroups={fetchMoreDeviceGroups}
            hasMoreDeviceGroups={hasMoreDeviceGroups}
            onManageDeviceClick={handleManageDeviceClick}
            selectedDevicesByDeviceGroup={selectedDevicesByDeviceGroup}
            loadingInitialData={isLoadingMyDeviceGroups}
            deviceGroupsCriteriaFilter={deviceGroupsFilterCriteria}
            appliedFilters={appliedFilters}
            isInvalidSelection={isInvalidSelection}
            disableDeviceGroupActions={true}
          />
        </Conditional.False>
      </Conditional>
    </BoxLayout>
  );
}

export function DeviceGroupsDashboardCardPage() {
  const { response } = useLoaderData() as DeferredResponse<
    [MyDevicesData, PaginatedAPIResponse<DeviceGroup>, DeviceModel[]]
  >;

  return (
    <ProtectedPage
      permissions={[
        DeviceGroupPermissions.DEVICE_GROUPS_READ,
        SoftwareSeatPermissions.SOFTWARE_SEATS_READ,
        SoftwareSeatPermissions.SOFTWARE_SEATS_ASSIGN,
      ]}
    >
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <DeviceGroupsDashboardCardPageContent />;
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default DeviceGroupsDashboardCardPage;
